import { Edit2, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import AppButton from "../../components/ui/AppButton";
import Pagination from "../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import { Brand, BrandSearchRequest, BrandSearchResponse } from "./@types";
import { useGetBrandsMutation } from "./brandApiSlice";
import BrandModal from "./components/BrandModal";
import DeleteBrandModal from "./components/DeleteBrand";

const BrandDashboard = () => {
  const [showModal, setShowModal] = useState("");
  const [selectedBrand, setSelectedFine] = useState<Brand | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [getBrands, { data: brandsData, isLoading }] = useGetBrandsMutation();

  const fetchBrands = useCallback(async () => {
    const requestBody: BrandSearchRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
    };

    try {
      await getBrands(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch brands:", err);
    }
  }, [getBrands]);

  useEffect(() => {
    fetchBrands();
  }, [currentPage, pageSize, fetchBrands]);

  // Table columns configuration
  const columns: Column<Brand>[] = [
    {
      key: "name",
      header: "Name",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "description",
      header: "Description",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "actions",
      header: "",
      width: "12",
      align: "center" as const,
      render: (value: any, item: Brand) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "Edit",
                onClick: () => {
                  setShowModal("edit");
                  setSelectedFine(item);
                },
                icon: <Edit2 className="w-4 h-4" />,
              },
              {
                label: "Delete",
                onClick: () => {
                  setShowModal("delete");
                  setSelectedFine(item);
                },
                icon: <Trash2 className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const response = brandsData as BrandSearchResponse | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-[10px] p-4">
          <div className="">
            <h2 className="text-xl font-semibold text-hColor">
              All vehicles brand
            </h2>
          </div>

          <div className="flex gap-3">
            <AppButton
              onClick={() => setShowModal("add")}
              className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/60 transition-colors min-w-[146px] h-[38px]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add brand
            </AppButton>
          </div>
        </div>

        {/* All brands section */}
        <div className="space-y-6 bg-white rounded-[10px] p-4">
          {/* Table */}
          <div className="overflow-hidden space-y-6">
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoading}
              emptyMessage="No brands found"
            />
          </div>
        </div>
        <div className="">
          {/* Pagination */}
          {response && (
            <Pagination
              currentPage={response.currentPage}
              totalPages={response.totalPages}
              pageSize={response.pageSize}
              totalCount={response.totalCount}
              hasNextPage={response.hasNextPage}
              hasPreviousPage={response.hasPreviousPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>
      </div>

      <BrandModal
        isOpen={showModal === "edit" || showModal === "add"}
        onClose={() => {
          setShowModal("");
          setSelectedFine(null);
          fetchBrands();
        }}
        brand={selectedBrand}
      />

      {selectedBrand && (
        <DeleteBrandModal
          fine={selectedBrand}
          isOpen={showModal === "delete"}
          onClose={() => {
            setShowModal("");
            setSelectedFine(null);
            fetchBrands();
          }}
        />
      )}
    </div>
  );
};

export default BrandDashboard;
